import { Button, Col, createModal, ModalProps, Row, useModal } from "@commonlibs";
import useTranslate from "ui/locale/hooks/useTranslate";
import clsx from "clsx";
import Modal from "ui/common/components/feedback/Modal/Modal";
import GiftCardForm from "./GiftCardForm";
import { useEffect, useState } from "react";
import { GiftCardFormDto, GiftCardFormDtoKey, giftCardformSchema } from "ui/giftcards/schemas/CreateGiftCard.schema";
import useCreateLocalGiftCard from "features/giftcards/hooks/useCreateLocalGiftcard";
import type { CreateLocalGiftcardDenominationRequest } from "common/models/api/models/CreateLocalGiftcardDenominationRequest";
import type { LocalGiftCardDto } from "common/models/api/models/LocalGiftCardDto";
import useUpdateLocalGiftCard from "features/giftcards/hooks/useUpdateLocalGiftCard";
import { useQueryClient } from "react-query";
import { Queries } from "ui/query/queries";
import { GiftCardStockStatus } from "common/models/api/models/GiftCardStock";

const GiftCardModal = createModal<{ giftCard?: LocalGiftCardDto } & ModalProps>(({ className, giftCard, ...rest }) => {
    const translate = useTranslate();
    const { hideModal } = useModal();
    const queryClient = useQueryClient();

    const { mutate: createGiftCard, isLoading: isCreatingGiftCard } = useCreateLocalGiftCard({ onSettled: closeModal });
    const { mutate: updateGiftCard, isLoading: isUpdatingGiftCard } = useUpdateLocalGiftCard({ onSettled: closeModal });

    useEffect(() => {
        queryClient.invalidateQueries(Queries.GET_GIFTCARD_CATEGORIES);
    }, [queryClient]);

    const [giftCardForm, setGiftCardForm] = useState<GiftCardFormDto>({
        id: giftCard?.id,
        partner: giftCard?.partner.address || "",
        imageUrl: giftCard?.imageUrl || "",
        category: giftCard?.category || "",
        termsAndConditions: giftCard?.termsAndConditions || "",
        type: (giftCard?.type as "onDemand" | "stock") ?? GiftCardStockStatus.onDemand,
        denominations: giftCard?.denominations.map((item) => item.value) || [],
        closingTime: giftCard?.closingTime || undefined,
        agreementCode: giftCard?.agreementCode || undefined,
    });

    function handleSetForm(field: GiftCardFormDtoKey, value: string | number[] | undefined) {
        setGiftCardForm((prev) => ({
            ...prev,
            [field]: value,
        }));
    }

    const isValidForm = () => {
        const result = giftCardformSchema.safeParse(giftCardForm);
        return result.success;
    };

    function buildGiftCardDenominations(): CreateLocalGiftcardDenominationRequest[] {
        return giftCardForm.denominations.map((item) => ({
            value: item,
            stock: null,
        }));
    }

    function handleSubmit() {
        const payload = {
            ...giftCardForm,
            denominations: buildGiftCardDenominations(),
            ...(giftCardForm.closingTime !== "" && { closingTime: giftCardForm.closingTime }),
        };
        if (giftCard) {
            updateGiftCard({ id: giftCard.id, payload });
            return;
        }
        createGiftCard(payload);
    }

    const isSendingRequest = () => isCreatingGiftCard || isUpdatingGiftCard;

    function closeModal() {
        hideModal(GiftCardModal.id);
    }

    return (
        <Modal title={translate("createGiftCard")} className={clsx("giftcards-form-modal", className)} style={{ width: "800px" }} {...rest}>
            <Col gap={30}>
                <GiftCardForm form={giftCardForm} onChange={handleSetForm} />
                <Row justifyContent="end" gap={15}>
                    <Button style={{ width: "220px" }} variant="outlined" onClick={closeModal}>
                        {translate("cancel")}
                    </Button>
                    <Button loading={isSendingRequest()} disabled={!isValidForm()} style={{ width: "220px" }} onClick={handleSubmit}>
                        {giftCard ? translate("edit") : translate("create")}
                    </Button>
                </Row>
            </Col>
        </Modal>
    );
});

export default GiftCardModal;
